import {useMemo} from 'react'
import {StyleSheet, View} from 'react-native'
import {Trans} from '@lingui/react/macro'

import {useAgent, useSessionApi} from '#/state/session'
import {agentToSessionAccount} from '#/state/session/agent'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {FormError} from '#/components/forms/FormError'
import {Text} from '#/components/Typography'
import {
  openWIdentityDeepLink,
  useQrDeepLink,
} from '../SignUpWizard/useQrDeepLink'
import {type QrSessionInitResult} from '../SignUpWizard/useQrSession'
import {useQrSession} from '../SignUpWizard/useQrSession'
import {FormContainer} from './FormContainer'

export function WidFlowMobile({
  error,
  onPressBack,
  // onAttemptSuccess,
  initialData,
}: {
  error: string
  onPressBack: () => void
  // onAttemptSuccess: () => void
  initialData?: QrSessionInitResult
}) {
  const t = useTheme()
  const {logoutEveryAccount, removeAccount} = useSessionApi()
  const agent = useAgent()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const account = useMemo(() => agentToSessionAccount(agent), [agent])

  const onLogout = () => {
    if (account) removeAccount(account)
    logoutEveryAccount('Settings')
    setShowLoggedOut(true)
  }

  const {qrCodeUrl, isInitError, initErrorMessage} = useQrSession({
    queryKey: 'remove-user-mobile',
    initFn: (signal, prevServiceId, prevSessionId) =>
      agent.account.requestDeleteAccount(
        {
          ...(prevServiceId && {serviceId: prevServiceId}),
          ...(prevSessionId && {sessionId: prevSessionId}),
        },
        {signal},
      ),
    initialData,
    statusFn: (params, signal) => agent.quicklogin.status(params, {signal}),
    onCompleted: data => {
      if (!data.approvalToken) return
      void agent.account
        .deleteAccount({approvalToken: data.approvalToken})
        .then(onLogout)
        .catch(() => {})
    },
    autoRefresh: true,
  })

  const deepLink = useQrDeepLink(qrCodeUrl)

  return (
    <FormContainer testID="loginForm" style={{backgroundColor: 'transparent'}}>
      <View style={[a.align_center, a.gap_md, a.py_md]}>
        <Text
          style={[
            a.text_sm,
            a.font_medium,
            t.atoms.text_contrast_medium,
            styles.labelText,
          ]}>
          <Trans>Remove user</Trans>
        </Text>
        <Text
          style={[
            a.text_xs,
            t.atoms.text_contrast_medium,
            {textAlign: 'center'},
          ]}>
          <Trans>Open the W Identity app to confirm account deletion.</Trans>
        </Text>
        {isInitError ? (
          <Text style={[a.text_xs, {color: t.palette.negative_500}]}>
            {initErrorMessage ?? 'Connection error. Please try again.'}
          </Text>
        ) : (
          <Button
            label="Open W Identity"
            color="primary"
            size="large"
            testID="openWidBtn"
            disabled={!deepLink}
            onPress={() => openWIdentityDeepLink(deepLink)}
            style={{alignSelf: 'stretch'}}>
            <ButtonText>
              <Trans>Open W Identity</Trans>
            </ButtonText>
          </Button>
        )}
      </View>
      <FormError error={error} />
      <View style={[a.flex_row, a.align_center, a.pt_md, a.self_center]}>
        <button className={'BackButton'} onClick={onPressBack}>
          <Trans>Back</Trans>
        </button>
        <View style={a.flex_1} />
      </View>
    </FormContainer>
  )
}

const styles = StyleSheet.create({
  labelText: {
    textAlign: 'center',
    fontSize: 16,
  },
})
